'use client'

import { Box, Flex, Group, Skeleton, Stack } from '@mantine/core'
import { useTranslations } from 'next-intl'
import React, { Suspense } from 'react'

import { SectionErrorBoundary } from '@/components/error/boundary'
import { useYieldProducts } from '@/services/invest'
import useGlobalStore from '@/stores/global'

import CardsContainer from '../CardsContainer'
import EarnCard from './EarnCard'

function EarnFallback() {
  return (
    <Box>
      <Flex justify="space-between" align="flex-end" w="100%" mb={24}>
        <Stack maw="calc(90% - 80px)" gap={8}>
          <Skeleton
            h={32}
            w={160}
            radius="xl"
            style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
          />
          <Skeleton
            h={24}
            w={400}
            radius="xl"
            style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
          />
        </Stack>
        <Skeleton
          h={44}
          w={120}
          radius="md"
          style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
        />
      </Flex>
      <Group wrap="nowrap" gap="md">
        <Skeleton
          h={180}
          w={300}
          radius="md"
          style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
        />
        <Skeleton
          h={180}
          w={300}
          radius="md"
          style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
        />
        <Skeleton
          h={180}
          w={300}
          radius="md"
          style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
        />
        <Skeleton
          h={180}
          w={300}
          radius="md"
          style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
        />
      </Group>
    </Box>
  )
}

function EarnContent() {
  const { data: yieldData } = useYieldProducts()
  const t = useTranslations('earn')

  const allCoins = useGlobalStore(state => state.allCoins)

  // Use Map to deduplicate by coin.symbol, keep the one with highest apr
  const coinMap = new Map()
  yieldData?.forEach(yieldCoin => {
    const searchCoin = allCoins.find(
      coin =>
        coin.symbol === yieldCoin.symbol ||
        coin.slug === yieldCoin.name ||
        coin.name === yieldCoin.name,
    )

    if (searchCoin) {
      const existingCoin = coinMap.get(searchCoin.symbol)
      const currentApr = Number(yieldCoin?.apr) || 0
      const existingApr = existingCoin ? Number(existingCoin?.apr) || 0 : 0

      // Keep the coin with higher apr
      if (!existingCoin || currentApr > existingApr) {
        coinMap.set(searchCoin.symbol, {
          ...searchCoin,
          icon_url: yieldCoin?.icon_url,
          apr: yieldCoin?.apr,
        })
      }
    }
  })

  const showCoins = Array.from(coinMap.values()).slice(0, 4)

  return (
    <CardsContainer
      title={t('title')}
      description={t('description')}
      data={showCoins}
      link="https://crypto.com/earn"
      render={(item, idx) => <EarnCard key={idx} {...item} />}
    />
  )
}

function Earn() {
  return (
    <Suspense fallback={<EarnFallback />}>
      <EarnContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(Earn)
